import { useEffect, useState, useCallback } from 'react';

const API_BASE = '/api/v1/admin';

export interface UseAdminAgentsState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Normalize various error body shape thanh string nguoi dung doc duoc.
 *
 * Backend co the tra ve:
 *  - `{ "error": "string ngan" }`
 *  - `{ "error": { "code": "X", "message": "Y", ... } }` (envelope error chuan)
 *  - `{ "message": "Y" }` hoac body rong
 *
 * Fix BUG 2026-05-12: truoc do dung `body.error ?? HTTP ${status}` -> khi error la object
 * thi `new Error(obj)` cho ra `e.message = "[object Object]"` render len FE.
 */
function extractErrorMessage(body: unknown, status: number): string {
  if (!body || typeof body !== 'object') return `HTTP ${status}`;
  const obj = body as Record<string, unknown>;
  const err = obj.error;
  if (typeof err === 'string' && err.trim()) return err;
  if (err && typeof err === 'object') {
    const inner = err as Record<string, unknown>;
    if (typeof inner.message === 'string' && inner.message.trim()) return inner.message;
    if (typeof inner.code === 'string') return inner.code;
  }
  if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
  return `HTTP ${status}`;
}

function toErrorString(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return 'Loi khong xac dinh';
  }
}

function getAuthHeader(): Record<string, string> {
  try {
    const token = localStorage.getItem('asklaw_access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function useAdminAgentsApi<T>(path: string): UseAdminAgentsState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${path}`, { headers: getAuthHeader() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(extractErrorMessage(body, res.status));
      }
      const json = (await res.json()) as T;
      setData(json);
    } catch (e) {
      setError(toErrorString(e));
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
